import { useEffect } from 'react';
import {
  Routes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { ROUTES_PATH } from '../constants/index';
import { DebtResultContextProvider } from '../contexts/DebtContext';
import { LoanResultContextProvider } from '../contexts/LoanContext/loan-context';
import { PaymentSelectedContextProvider } from '../contexts/PaymentSelectedContext';
import { UserResultContextProvider } from '../contexts/UserContext';
import Entry from '../pages/Entry/Entry';
import Payment from '../pages/Payment/Payment';
import PaymentCheck from '../pages/PaymentCheck/PaymentCheck';
import Success from '../pages/FinalModal/Success/Success';
import Tpv from '../pages/Tpv/Tpv';
import UnknownError from '../pages/FinalModal/UnknownError/UnknownError';
import { Transition } from './../pages/Transition/Transition';
import { InsightTacking } from '@pepper/insights-react';
import NoDebt from '../pages/FinalModal/NoDebt/NoDebt';
import { CardsResultContextProvider } from '../contexts/CardsContext';
import { LegalNotice } from '../pages/LegalNotice';
import { PrivacyPolicy } from '../pages/PrivacyPolicy';
import { CookiesPolicy } from '../pages/CookiesPolicy';
import EntryCO from '../pages/EntryCO/EntryCO';
import Timeout from '../pages/FinalModal/Timeout/Timeout';
import PaymentOnCourse from '../pages/FinalModal/PaymentOnCourse/PaymentOnCourse';
import { LedgerResultContextProvider } from '../contexts/LedgerContext';
import { LandingOverlay } from '../components/LandingOverlay/LandingOverlay';
import GAnalyticsService from '../services/GAnalytics/ganalytics.service';
import { ViewMode } from '../interfaces/Mode';
import VerifyCardTpv from '../pages/VerifyCardTpv/VerifyCardTpv';
import CardUpToDate from '../pages/FinalModal/CardUpToDate/CardUpToDate';
import CoClientError from '../pages/FinalModal/CoClientError/CoClientError';

const AppRoutes: React.FC = () => {
  const {
    ENTRY,
    ENTRY_CO,
    TYPES_PAYMENTS,
    TRANSITION,
    UNKNOWN_ERROR,
    TPV,
    SUCCESS,
    PAYMENT_CHECK,
    NO_DEBT,
    LEGAL_NOTICE,
    PRIVACY_POLICY,
    COOKIES_POLICY,
    TIMEOUT,
    PAYMENT_ON_COURSE,
    VERIFY_CARD_TPV,
    CARD_UP_TO_DATE,
    CO_CLIENT_ERROR,
  } = ROUTES_PATH;
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const search = useLocation().search;
  const urlParams = new URLSearchParams(search);
  const paymentCheck = new URLSearchParams(search).get('payment');
  const entryCo = urlParams.get('entry-co');
  let viewMode: ViewMode | null = null;
  const storedViewMode = sessionStorage.getItem('pepper-view-mode');
  const viewModeParam = urlParams.get('viewMode')?.toString().toUpperCase();

  // si no hay viewMode almacenado en el sessionStorage
  if (!storedViewMode) {
    // si hay viewModeParam, se almacena en el sessionStorage
    if (viewModeParam) {
      sessionStorage.setItem('pepper-view-mode', viewModeParam);
      viewMode = viewModeParam as ViewMode;
    }
  } else {
    //si el viewModeParam es distinto al almacenado en el sessionStorage, se actualiza
    if (viewModeParam && viewModeParam !== storedViewMode) {
      sessionStorage.setItem('pepper-view-mode', viewModeParam);
      viewMode = viewModeParam as ViewMode;
    } else {
      viewMode = storedViewMode as ViewMode;
    }
  }

  const documentRoute = () => {
    return (
      pathname === ROUTES_PATH.LEGAL_NOTICE ||
      pathname === ROUTES_PATH.COOKIES_POLICY ||
      pathname === ROUTES_PATH.PRIVACY_POLICY
    );
  };

  const documentRouteMobileBackground = () => {
    return (
      pathname.includes(ROUTES_PATH.TIMEOUT) ||
      pathname === ROUTES_PATH.TRANSITION ||
      pathname === ROUTES_PATH.DENIAL ||
      pathname.includes(ROUTES_PATH.UNKNOWN_ERROR) ||
      pathname === ROUTES_PATH.UNPROCESSABLE ||
      pathname.includes(ROUTES_PATH.SUCCESS) ||
      pathname === ROUTES_PATH.PAYMENT_CHECK ||
      pathname.includes(ROUTES_PATH.NO_DEBT) ||
      pathname === ROUTES_PATH.PAYMENT_ON_COURSE
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (pathname.includes(ROUTES_PATH.PAYMENT_CHECK) && paymentCheck) {
      if (paymentCheck === 'OK') {
        navigate(ROUTES_PATH.SUCCESS);
      } else {
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (urlParams.size > 0) {
      GAnalyticsService.saveSessionDataLayer(urlParams || '');
    } else {
      GAnalyticsService.saveSessionDataLayer(new URLSearchParams());
    }

    if (entryCo) {
      sessionStorage.setItem('entryCo', 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DebtResultContextProvider>
      <LoanResultContextProvider>
        <PaymentSelectedContextProvider>
          <UserResultContextProvider>
            <LedgerResultContextProvider>
              <CardsResultContextProvider>
                <InsightTacking
                  connectionString={
                    process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING ?? ''
                  }
                />
                <LandingOverlay
                  iframeView={viewMode === ViewMode.IFRAME}
                  documentPage={documentRoute()}
                  documentPageBackground={documentRouteMobileBackground()}
                  viewMode={viewMode}
                >
                  <Routes>
                    <Route path={ENTRY} element={<Entry />} />
                    <Route path={ENTRY_CO} element={<EntryCO />} />
                    <Route path={TYPES_PAYMENTS} element={<Payment />} />
                    <Route path={TPV} element={<Tpv />} />
                    <Route path={VERIFY_CARD_TPV} element={<VerifyCardTpv />} />
                    <Route path={TRANSITION} element={<Transition />} />
                    <Route path={UNKNOWN_ERROR} element={<UnknownError />} />
                    <Route path={SUCCESS} element={<Success />} />
                    <Route path={PAYMENT_CHECK} element={<PaymentCheck />} />
                    <Route path={NO_DEBT} element={<NoDebt />} />
                    <Route path={LEGAL_NOTICE} element={<LegalNotice />} />
                    <Route path={PRIVACY_POLICY} element={<PrivacyPolicy />} />
                    <Route path={COOKIES_POLICY} element={<CookiesPolicy />} />
                    <Route path={TIMEOUT} element={<Timeout />} />
                    <Route
                      path={PAYMENT_ON_COURSE}
                      element={<PaymentOnCourse />}
                    />
                    <Route path={CARD_UP_TO_DATE} element={<CardUpToDate />} />
                    <Route path={CO_CLIENT_ERROR} element={<CoClientError />} />
                    <Route path="*" element={<Timeout />} />
                  </Routes>
                </LandingOverlay>
              </CardsResultContextProvider>
            </LedgerResultContextProvider>
          </UserResultContextProvider>
        </PaymentSelectedContextProvider>
      </LoanResultContextProvider>
    </DebtResultContextProvider>
  );
};

export default AppRoutes;
